import { ChangeDetectorRef, Component, DOCUMENT, EnvironmentInjector, inject, OnInit, runInInjectionContext, ViewChildren } from '@angular/core';
import { FocusedFormComponent } from "../../component/wrapper/focused-form.component";
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, filter, lastValueFrom, take, takeUntil } from 'rxjs';
import { AuthUserActions, authUserFeature } from '../../store/authUser.store';
import { AccountActions, accountFeature, AccountState } from '../../store/account.store';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { addDisplayNameAndMobileToUser, getAllActiveSegmentsPublic, getUserSegments } from '@dataconnect/account';
import { AuthUser } from '../../../../globalTypes/auth.types';
import { COLLECTION_PATH_ACCOUNT_CONFIG, FireStoreStripeAccountRecord, LocationPrivate, LocationPublic, LocCreationMode, SegmentPublic } from '../../../../globalTypes/account.types';
import { MatSelectModule } from '@angular/material/select';
import { AccountService } from '../../services/account.service';
import { ManageLocationsFormComponent } from "../../component/forms/manage-locations-form.component";
import { AuthService } from '../../services/auth.service';
import { Logger } from '../../helper/logger';
import { preRegRequirementsTests, TestReturn } from './pre-reg-requirements-test';
import { doc, Firestore, onSnapshot, Unsubscribe} from '@angular/fire/firestore';
import { } from 'firebase/firestore';
import { OnBoardingComponent } from './onBoarding.component';
import JSConfetti from 'js-confetti';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getObjectDifferences } from '../../helper/general';
import { Auth } from '@angular/fire/auth';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { getSubdomain } from '../../../../globalTypes/_helpers';
import { httpsCallable } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { JsonPipe } from '@angular/common';
import { Router } from '@angular/router';
import { FormInputPhoneNumberComponent } from "../../component/forms/form-input-phone-number.component";
import { LimiterDirective } from '../../directive/limiter';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';
const log = new Logger("ManageAccountComponent");

type Mode =  "error" | "regForm" | "joinSeg" | "manageLoc" 

@Component({
  selector: 'msh-create-account',
  imports: [FocusedFormComponent, OnBoardingComponent, MatSelectModule, FormsModule, ManageLocationsFormComponent, LoadingLogoComponent, ButtonComponent, FormInputPhoneNumberComponent, LimiterDirective],
  providers: [],
  template: `

  @if(onBoardingInfo){
    <msh-onboarding [onBoardingInfo]="onBoardingInfo" [firstTime]="justCreatedFirstLocation"></msh-onboarding>
  } 
  @if(needsToRetryPaymentProcessorSetup){
    <div class="flex flex-col item-center justify-center text-center h-96 mb-5 ">
      <div>There was an issue setting up the payment processor.  Please retry.</div>
      <msh-button [isLoading]="isLoading" class="mt-5" (click)="retryPaymentProcessorSetup(accountState?.location?.id || '')" cta="Retry Payment Setup"></msh-button>
      <div class=" text-red-500 mt-4">{{retryError}}</div>
    </div>
  }
  @if(creationStage) {
    <div class="text-center uppercase h-96 mb-5 ">
      <msh-loading-logo class=" w-48 h-48 mx-auto"></msh-loading-logo>
      <div class=" relative -top-6">
        {{creationStage}}
      </div>
    </div>
  }
  <msh-focused-form 
    [hideSubmitButton]="hideSubmitButton || !!creationStage" 
    [isLoading]="isLoading" 
    (onSubmit)="onSubmit($event)" 
    [disabled]="mode==='error' || creatingStages.includes(creationStage) || testFailureMessages.length > 0 || (mode === 'regForm' && (!newDisplayName && !authUser?.displayName))" 
    [cta]="cta"
  >
    <div heading >{{heading}}</div>
    @if(mode === 'joinSeg' 
      && accountState 
      && accountState.availableSegments
      ; as segments){
      <mat-select class="w-full mb-3 px-3 py-2 rounded border border-navy/15" [(ngModel)]="selectedSeg" (selectionChange)="segSelected()">
        @for(seg of segments; track seg.id){
          <mat-option class=" text-copy" [value]="seg">{{seg.name}}</mat-option>
        }
      </mat-select>

      @if(this.selectedSeg && this.selectedSeg.segmentRegistrationRequirements && this.selectedSeg.segmentRegistrationRequirements.length > 0 && this.selectedSeg.segmentRegistrationRequirements; as rules){
        <div>Market segment registration requirements:</div>
        <ul class="list-disc pb-6">
        @for(rule of rules; track rule.id){
          <li class="ml-3">@if(rule.userMustMeet){Required:  }@else if(rule.verifiedUserMustMeet){Verified:  }{{rule.ruleDescription}} </li>
        }
        </ul>
        @if(testFailureMessages.length){
          <div class=" text-red-600">Unfortunately you did not pass requirements:</div>
          <div class="text-sm mb-10 mt-2">
          @for(testFailureMessage of testFailureMessages; track testFailureMessages){
            <div>{{testFailureMessage}}</div>
          }
          </div>
        }
        @if(verifiedSuccessMessage.length){
          <div class=" text-green-600">{{verifiedSuccessMessage}}</div>
        }
      }
    }
    @if(mode === 'manageLoc' ){
      <msh-manage-locations-form 
        #manageLocationsForm
        [shippingSameAsPhysical]="!!authUser && authUser.isVerifiedForSegment && !!selectedSeg && !!selectedSeg.id && authUser.isVerifiedForSegment[selectedSeg.id]"
        [segmentId]="selectedSeg ? selectedSeg.id : (accountState && accountState.segment ? accountState.segment.id : 0)"
        [locations]="accountState?.availableLocations || []"
        [hideForm]="!!creationStage"
        [defaultLocationId]="authUser?.defaultLocationId"
        (onCreateProgress)="updateLocationCreationMode($event)"
        ></msh-manage-locations-form>
    }
    @if(mode === 'regForm'){
      @if(!authUser?.displayName){ 
        <div>
          <input
            type="text"
            placeholder="Full Name"
            [(ngModel)]="newDisplayName"
            [limiter]="150"
            name="displayName"
            required
          />
        </div>
      }
      @if(!authUser?.mobile){

        <msh-form-input-phone-number
          [placeholder]="'Mobile Phone Number'"
          [phoneNumber]="newMobile"
          (onImmediatePhoneNumberChange)="newMobile = $event"
          (onPhoneNumberChange)="newMobile = $event"
          required
        >
        </msh-form-input-phone-number>
        <div class="text-sm null relative -top-6">(only used to communicate the status of an order)</div>
      }
      @if(!authUser?.isEmailVerified){
        <div class="mb-7">
        @if(verificationEmailSent){
          You will be redirected once you verify your email. <span class=" text-orange-400">NOTE: </span> <span class=" text-orange-400 font-thin">Please check your spam folder if you don't see the email within a few minutes.</span>
        } @else {
          You will be sent a verification email once you submit this form.
        }
        </div>
      }
    }
    @if(mode=== 'error'){
      <div>There seems to have been an error. Please try refreshing the page.</div>
    }
  </msh-focused-form>

  `,
  styles: ``
})
export class ManageAccountPageComponent extends Base<ManageAccountPageComponent> implements OnInit {
  @ViewChildren('manageLocationsForm') manageLocationsForm!: ManageLocationsFormComponent;
  one:string= '';
  heading: string = "";
  retryError = "";
  cta: string = "submit";
  isLoading = true;
  justCreatedFirstLocation = false;
  mode!: Mode
  authUser: AuthUser | null = null;
  accountState: AccountState | null = null;
  selectedSeg!: SegmentPublic;
  selectedLoc!: LocationPublic;
  locationAnswers: string[] = [];
  hideSubmitButton: boolean = false;
  newDisplayName = '';
  newMobile = '';
  verificationEmailSent = false;
  emailClickedCheckLoop!: ReturnType<typeof setInterval>;
  testFailureMessages: string[] = [];
  verifiedSuccessMessage: string = '';
  onBoardingInfo?: FireStoreStripeAccountRecord
  creationStage: LocCreationMode | undefined 
  creatingStages: (LocCreationMode | undefined)[] = ["start", "creating record", "attaching address", "logging details", "initiating payment processor", "waiting for response"]
  stripeUserUnSub!: Unsubscribe
  envInjector = inject(EnvironmentInjector);
  document: Document = inject(DOCUMENT);
  functions = inject(Functions);
  needsToRetryPaymentProcessorSetup = false;
  justCreatedAccount = false;
  creatingAccount = false;

  constructor(
    private store: Store,
    private cd: ChangeDetectorRef,
    private db: Firestore,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private auth: Auth,
    private router: Router,
    private accountService: AccountService,
  ){ super() }
  
  async ngOnInit(): Promise<void> {
    combineLatest([
      this.store.select(authUserFeature.selectUser),
      this.store.select(accountFeature.selectAccountState),
    ]).pipe(
      takeUntil(this.$destroy), 
      filter(([authUser, accountState])=>!!authUser && !!accountState),
      debounceTime(1000),
    ).subscribe( async ([authUser, accountState]) => {
      const userDiff = getObjectDifferences(this.authUser || {}, authUser || {});
      const stateDiff = getObjectDifferences(this.accountState || {}, accountState || {});
      console.log({m:"-----------------------", authUser, accountState, userDiff, stateDiff})
      if(authUser && !authUser.defaultLocationId){
        // check invites here      
        const hasReceivedInvites = await lastValueFrom(this.store.select(accountFeature.selectInvitationsReceived).pipe(take(1)));
        if(hasReceivedInvites.length > 0){
         this.accountService.acceptInvite(hasReceivedInvites[0]);
         this.router.navigate(['/listing/search'])
        }
      }
// FIXME! Make location question mandatory
      this.authUser = authUser;
      this.accountState = accountState;
      console.log({accountState})
      // FIXME call auth service to check if email is verified, if both FB and DB verification statuses don't match
      if(accountState.userStatusId === "initiated" && authUser && authUser.isEmailVerified){
        await this.authService.emailVerifiedCheck();
      }
      let currentSeg = accountState.segment;
      const subdomain = getSubdomain(this.document);
      if(subdomain && !accountState.segment){
        const seg = accountState.availableSegments.find(s => s.subdomain === subdomain);
        if(seg){
          this.selectedSeg = seg;
          this.segSelected();
          currentSeg = seg;
          if(this.testFailureMessages.length === 0){
            await this.onSubmit();
            this.updateMode("manageLoc");
          }
        }
      }
      
      const mode: Mode = !accountState ? "error"
        : ( !this.authUser?.isEmailVerified || !this.authUser?.displayName ? "regForm" 
        : (  !currentSeg && accountState.availableLocations.length === 0 ? "joinSeg"
        : "manageLoc" ));
      console.log({mode, tMode: this.mode,
        verified: this.authUser?.isEmailVerified,
        name: this.authUser?.displayName,
        currentSeg,
        segment: accountState.segment,
        avLoc: accountState.availableLocations.length,
        avSeg: accountState.availableSegments.length,
      });
      console.log({location: this.accountState.location, authUser: this.authUser?.defaultLocationId})
      runInInjectionContext(this.envInjector, ()=>{
        // get payment processor status sync
        console.log({accountState: this.accountState, location:  this.accountState && this.accountState.location, authUser: this.authUser?.defaultLocationId})
        if(this.accountState && this.accountState.location && this.authUser?.defaultLocationId === this.accountState.location.id){
          // TODO: setup logout listener that unsubscribes on logout
            const stripeUserRef = doc(this.db, COLLECTION_PATH_ACCOUNT_CONFIG + "/" + this.accountState.location.id)
            console.log({msg:" ready to subscribe to onboarding data "})
            this.stripeUserUnSub = onSnapshot(stripeUserRef, { next: (snapshot) => {
            console.log({msg:" subscribed to onboarding data "+ snapshot.exists()})
              if(snapshot.exists()){
                this.needsToRetryPaymentProcessorSetup = false;
                this.onBoardingInfo = snapshot.data() as FireStoreStripeAccountRecord;
                if([ "waiting for response","initiating payment processor"].includes(this.creationStage || '') && this.onBoardingInfo.accountId){
                  this.updateLocationCreationMode("complete");
                  this.isLoading = false;
                  this.stripeUserUnSub();
                }
              }else{
                console.log('no onboarding info exists')
                this.needsToRetryPaymentProcessorSetup = !this.creatingAccount;
              }
            }})
        }
      })
      this.updateMode(mode);
      if(subdomain && this.selectedSeg 
        && this.selectedSeg.subdomain === subdomain 
        && mode === "joinSeg"
      ){
        await this.onSubmit();
        this.updateMode("manageLoc");
      }
    })
  }

  updateLocationCreationMode(mode: LocCreationMode){
    this.isLoading = true;
    console.log({mode})
    this.creatingAccount = true;
    switch(mode){
      case "complete":
        this.creationStage = undefined;
        this.isLoading = false;
        const jsConfetti = new JSConfetti()
        jsConfetti.addConfetti({
         // emojis: ['💰', '💥', '🔪','💲'],// , '⚡️', '🍴', '💫'
          confettiColors: [
            '#E48133', '#b35d21', '#121e27', '#2C404F', '#ffd59e', '#ffe8c7',
          ],
        })
        this.cd.markForCheck();
        this.justCreatedAccount = true;
        this.justCreatedFirstLocation = !this.accountState?.locations || Object.keys(this.accountState?.locations).length <= 1;
        break;
      default:
        this.creatingAccount = true;
        if(this.justCreatedAccount) return; // once complete, don't go back to previous stages
        this.creationStage = mode;
    }
  }

  async retryPaymentProcessorSetup(locationId: string){
    this.isLoading = true;
    this.retryError = "";
    try {
      await this.accountService.setupPaymentProcessor(locationId);
      this.isLoading = false;
    } catch(error){
      log.warn('error setting up payment processor')
      log.error({error})
      this.retryError = "Error occurred. Please try again.";
      this.isLoading = false;
      return;
    }
  }

  async updateMode(newMode: Mode){
    this.isLoading = false;
    console.log({newMode})
    this.cta="submit";
    this.hideSubmitButton = false;
    switch(newMode){
      case "joinSeg":
        this.heading = "Select a Segment";
        let availableSegments = await lastValueFrom(this.store.select(accountFeature.selectAvailableSegments).pipe(take(1)))
        log.log({availableSegments})
        if(!availableSegments || availableSegments.length === 0){
          const segListResults = await getAllActiveSegmentsPublic()
          const segList = segListResults.data;
          this.store.dispatch(AccountActions.setAvailableSegments({ availableSegments: segList.segments as SegmentPublic[]}))
          console.log({segList});
          availableSegments = segList.segments as unknown as SegmentPublic[];
        }
        if(availableSegments.length === 1 && !this.selectedSeg){
          this.selectedSeg = availableSegments[0];
          this.segSelected();
        }
        break;
      case "regForm":
        // if(!this.accountState?.segment?.id) return;
        this.heading = "Please verify your email address";
        this.cta = "Verify";
        if(!this.authUser?.isEmailVerified && this.verificationEmailSent){
          this.isLoading = true;
        }

        if(!this.authUser?.displayName){ 
          this.heading = "Tell us more about you";
          this.cta = "Submit";
          if(!this.authUser?.isEmailVerified){
            this.cta = "Submit & Send Verification Email"
          }
        }
        break;
      case "manageLoc":
        if(!this.accountState) return;
        const locsAmount = this.accountState.availableLocations.length;
        log.log({locsAmount})
        const seg = (this.accountState.segment ? this.accountState.segment.name : '') +" "+ LOCATION_LABEL;
        switch(locsAmount){ // TODO change title when possibilities changes
          case 0: this.heading = "Build your  "+seg; break;
          case 1: this.heading = "Manage your "+seg; break;
          default: this.heading = "Switch to a different "+seg; break;
        }
        this.hideSubmitButton = true;
        break;
      case "error":
        this.heading = "Hmmm.....";
        break;
    }
    if(this.mode === newMode) return;
    this.mode = newMode;
    // [ ] this might need to be reduced to only 1 or 2 cases // await this.accountService.loadMyAccountInfo('update manage account mode: '+ this.mode);
  };

  segSelected(){
    this.testFailureMessages = [];
    if(!this.selectedSeg || !this.selectedSeg.segmentRegistrationRequirements) return;
    this.selectedSeg.segmentRegistrationRequirements.sort((a,b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    for (let i = 0; i < this.selectedSeg.segmentRegistrationRequirements.length; i++) {
      const rule = this.selectedSeg.segmentRegistrationRequirements[i];
      const key = rule.requirementValue as keyof typeof preRegRequirementsTests;
      if(rule.userMustMeet && preRegRequirementsTests[key] && this.authUser){
        const testResults: TestReturn = preRegRequirementsTests[key](this.authUser, this.snackBar);
        log.log(testResults)
        if(!testResults.success) {
          this.testFailureMessages.push(testResults.failureMsg || 'Failed requirement: '+ rule.ruleDescription);
        }
      }
      if(rule.verifiedUserMustMeet && preRegRequirementsTests[key] && this.authUser){
        const testResults: TestReturn = preRegRequirementsTests[key](this.authUser, this.snackBar);
        log.log(testResults)
        if(!testResults.success) {
          this.verifiedSuccessMessage = testResults.successMsg || "Verified user";
        }
      }
    }
  }
  
  async onSubmit(ev?: Event){
    this.isLoading = true;
    if(ev) ev.preventDefault();
    
    switch(this.mode){
      case "joinSeg":
        this.isLoading = true;
        try{
          await this.auth.currentUser?.reload();
          const callable = httpsCallable(this.functions,  onCallFunctions.createUserSegment);
          await callable({segmentId: this.selectedSeg.id});
          
          // const userSegmentRecord = await uiErrorCatcher( createUserSegment(createUserSegmentVars), this.snackBar, "Error creating user segment");
          // // FIXME! if seg doesn't == subdomain go to login
          // console.log({userSegmentRecord});
          // FIXME HANDLE "unauthenticated: this operation requires a signed-in user" error if you just verified email
        } catch(error){
          const currentSegments = await getUserSegments();
          this.isLoading = false;
          log.log({currentSegments})
          log.warn('cant make user segment')
          log.error({error})
          this.mode = "error";
          this.snackBar.open("Error creating segment user. Please try refreshing the page.", "Close", {duration: 5000})
          return;
        }
        this.updateMode('manageLoc')
        break;
      case "regForm":
        let userUpdate: Partial<AuthUser> = {};
        userUpdate['displayName'] = this.newDisplayName;
        userUpdate['mobile'] = this.newMobile; 
        
        if(Object.keys(userUpdate).length > 0 && this.authUser?.id ){
          userUpdate['id'] = this.authUser?.id;
          await this.authService.saveUser(userUpdate as Pick<AuthUser, 'id'> & Partial<AuthUser>);
          await addDisplayNameAndMobileToUser({displayName: this.newDisplayName, mobile: this.newMobile})
        }
        if(!this.authUser?.isEmailVerified){
          this.heading = "Verification email sent";
          await this.authService.sendEmailVerificationLink(this.authUser?.email || '');
          this.authService.emailVerifiedCheck();
          this.verificationEmailSent = true;
          this.startEmailClickedCheckLoop();
        }
        break;
      case "manageLoc":
        // FIXME make mandatory location/segment questions
        console.log('manage loc submit')
        break;
    }
  }
  onSelectLocation(){
    // update firestore user defaultLocation
        this.store.dispatch(AuthUserActions.setDefaultLocation({locationId: this.selectedLoc.id}));
        this.store.dispatch(AccountActions.setLocation({location: this.selectedLoc as LocationPrivate}));
  }

  startEmailClickedCheckLoop(){
    this.emailClickedCheckLoop = setInterval(async () => {
      const isEmailVerified = await this.authService.emailVerifiedCheck();
      if(isEmailVerified && this.authUser){
        clearInterval(this.emailClickedCheckLoop)
        this.authService.logout();
      }
      log.log({emailClickedCheckLoop: isEmailVerified})
    }, 1000 * 2)
  }
  
  override ngOnDestroy(): void {
    if(this.stripeUserUnSub) this.stripeUserUnSub();
  }
}
